<template>
    <div>
        <!-- Visibility belongs to the transition controller; backdrop/Escape must not release a quiz. -->
        <e-modal :model-value="isStatusModalOpen" :close-on-escape="false" :can-cancel="false" full-screen :pt="{ pcMaximizeButton: { root: { style: 'display: none' } } }">
            <quiz-status :status="waitingRoomAttempt?.status" :color="color" :activity-id="activityId" :attempt-id="waitingRoomAttempt?.quiz_attempt_id" />
        </e-modal>

        <e-modal :model-value="isRedirectPromptOpen" :close-on-escape="false" :can-cancel="false" style="z-index: 10000">
            <quiz-attempt-redirect-modal
                :quiz-attempt-id="quizAttempt?.user?.quiz_active_id || -1"
                :status="quizAttempt?.status"
                @close="closeRedirectPrompt"
            />
        </e-modal>

        <e-modal :model-value="isClosingModalOpen" :close-on-escape="false" :can-cancel="false" style="z-index: 10001">
            <quiz-attempt-redirect-modal
                :quiz-attempt-id="quizAttempt?.user?.quiz_active_id || -1"
                closing
                @close="closeQuizAttempt"
            />
        </e-modal>
        <e-modal v-model="hasTransitionError" :can-cancel="false" style="z-index: 10002">
            <div class="tw:p-6" role="alert">
                <p>{{ transitionErrorMessage }}</p>
                <e-button label="Retry and continue" :loading="retryingTransition" @click="retryTransition" />
            </div>
        </e-modal>
    </div>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import QuizStatus from '@/components/quiz-attempt/quiz-status/QuizStatus.vue';
import QuizAttemptRedirectModal from '@/components/quizzes/QuizAttemptRedirectModal.vue';
import { useQuizAttemptDiscovery } from '@/composables/useQuizAttemptDiscovery.js';
import { useSocketConnection } from '@/composables/useSocketConnection.js';
import { applyQuizStatus, quizStatusRevision, registerQuizStatusController } from '@/services/QuizStatusTransition.js';
import quizWaitingRoomService from '@/services/quizWaitingRoomService.js';
import { findQuizAttemptModule, synchronizeQuizStatus } from '@/utilities/synchronizeQuizStatus.js';

const props = defineProps({
    onlineResponse: {
        type: Object,
        default: () => ({}),
    },
});

const $store = useStore();
const $route = useRoute();
const $router = useRouter();

const isRedirectPromptOpen = ref(false);
const isStatusModalOpen = ref(false);
const isClosingModalOpen = ref(false);
const quizAttempt = ref(null);
// Keep the last waiting-room screen beneath the redirect until navigation completes.
const waitingRoomAttempt = ref(null);
const lobbyAttemptId = ref(null);
const hasTransitionError = ref(false);
const transitionErrorMessage = ref('');
let retryAnswerSave = false;
const retryingTransition = ref(false);
let pendingTransition = null;
let transitionSequence = 0;
let disposed = false;
const color = computed(() => $store.getters['User/preferredTheme']());
const { user } = $store.state.Auth;

const activityId = computed(() => $route.params.activityId);

// Listen for forced quiz status
const channel = `quiz-attempt-status-${user.id}`;

const openStatusModal = attempt => {
    quizAttempt.value = attempt;
    waitingRoomAttempt.value = attempt;
    isStatusModalOpen.value = true;
};

const openRedirectPrompt = attempt => {
    quizAttempt.value = attempt;

    isRedirectPromptOpen.value = true;
};

const closeRedirectPrompt = () => {
    // The Start dialog emits close only after the current attempt is hydrated.
    isRedirectPromptOpen.value = false;
    isStatusModalOpen.value = false;
    waitingRoomAttempt.value = null;
};

const closeQuizAttempt = async () => {
    isClosingModalOpen.value = false;

    // Compute safe redirect targets from route first, then user store fallback
    const userActivity = $store.getters['User/get']('quiz_activity');
    const fallbackActivityId = userActivity?.activity?.id ?? userActivity?.id ?? null;
    const fallbackCourseId = userActivity?.activity?.course?.id ?? userActivity?.course?.id ?? null;

    const resolvedActivityId = $route.params.activityId ?? fallbackActivityId;
    let courseId = $route.params.courseId ?? fallbackCourseId;

    // If we have activityId but not courseId, try to resolve from Activities store or fetch
    if (resolvedActivityId && !courseId) {
        const activityFromStore = $store.getters['Activities/getByKey']({
            keyName: 'id',
            key: Number(resolvedActivityId),
        });

        courseId =
            activityFromStore?.course?.id ??
            activityFromStore?.activity?.course?.id ??
            activityFromStore?.course_id ??
            null;

        if (!courseId) {
            try {
                const resp = await $store.dispatch('Activities/fetchOne', resolvedActivityId);
                const data = resp?.data?.data;
                courseId = data?.course_id ?? data?.course?.id ?? null;
            } catch (_e) {
                // ignore and fall back to best-effort redirect below
            }
        }
    }

    // If we have both IDs, go back to activity view; otherwise, go home
    const target =
        resolvedActivityId && courseId
            ? { name: 'activity-view', params: { activityId: resolvedActivityId, courseId } }
            : { name: 'home' };

    const { href } = $router.resolve(target);
    window.location.href = href;
};

// The attempt id a broadcast would have carried, for a page session that never
// received one. Mid-quiz the student arrived by a full page load
// (QuizAttemptRedirectModal navigates with window.location.href), so
// `lobbyAttemptId` is still null and the reconnect re-sync below could never
// make a single request. `quiz_active_id` is the same value the broadcast
// payload carries (`event.user.quiz_active_id`) and the server ships it with
// the initial page state.
const activeAttemptId = () => Number($route.params.attemptId) || Number($store.state.Auth.user?.quiz_active_id) || null;

const handleStatusEvent = async incomingEvent => {
    const event = { ...incomingEvent };
    const eventAttemptId = Number(event.quiz_attempt_id) || Number(event.user?.quiz_active_id) || activeAttemptId();
    const onScreenAttemptId = activeAttemptId();
    if (eventAttemptId && onScreenAttemptId && eventAttemptId !== onScreenAttemptId) return false;

    const sequence = ++transitionSequence;
    const current = () => !disposed && sequence === transitionSequence;
    const transition = { ...event, quiz_attempt_id: eventAttemptId };
    let synchronized;
    retryAnswerSave = false;
    try {
        synchronized = await synchronizeQuizStatus($store, transition, current);
        retryAnswerSave = !synchronized;
        transitionErrorMessage.value =
            'Your quiz status changed, but an answer could not be saved. Your answers are still on this page.';
    } catch {
        synchronized = false;
        transitionErrorMessage.value = 'Unable to load quiz review. Please try again.';
    }
    if (!current()) return false;
    if (!synchronized) {
        pendingTransition = transition;
        hasTransitionError.value = true;
        return false;
    }
    pendingTransition = null;
    hasTransitionError.value = false;
    // Repeated fallback polls must not restart the student's countdown.
    if (
        quizAttempt.value?.status === event.status &&
        Number(quizAttempt.value?.quiz_attempt_id) === eventAttemptId &&
        (isStatusModalOpen.value || isRedirectPromptOpen.value || isClosingModalOpen.value)
    )
        return true;

    event.quiz_attempt_id = eventAttemptId;
    lobbyAttemptId.value = eventAttemptId;
    // Start/End prompts must retain the waiting room underneath them. Review
    // can reveal the page immediately because synchronization loaded it above.
    isRedirectPromptOpen.value = false;
    isClosingModalOpen.value = false;
    if (['in-waiting-room', 'ready-to-start', 'in-waiting-room-post'].some(check => check === event.status)) {
        $store.commit('Auth/UPDATE_QUIZ_ACTIVE_ID', event.user.quiz_active_id);
        $store.commit('User/SET', { field: 'quiz_active_id', value: event.user.quiz_active_id });

        openStatusModal(event);
    }

    if (event.status === 'attempt-started') {
        await openRedirectPrompt(event);
    }

    if (event.status && event.status.includes('review')) {
        quizAttempt.value = event;
        isStatusModalOpen.value = false;

        // synchronizeQuizStatus already applied review to the matching attempt.
    }

    if (!event.status) {
        // Keep the waiting room covered through the countdown and redirect.
        quizAttempt.value = event;
        isClosingModalOpen.value = true;
    }
    return true;
};

const retryTransition = async () => {
    if (!pendingTransition || retryingTransition.value) return;
    retryingTransition.value = true;
    const event = pendingTransition;
    try {
        const moduleId = findQuizAttemptModule($store, event.quiz_attempt_id);
        if (
            retryAnswerSave &&
            moduleId !== null &&
            (await $store.dispatch(`QuizAttempt/${moduleId}/finalizeCurrentAnswers`)) === false
        )
            return;
        await applyQuizStatus(event);
    } finally {
        retryingTransition.value = false;
    }
};

const unregisterStatusController = registerQuizStatusController(handleStatusEvent);

// Waiting-room polling fallback: status transitions normally arrive over the
// websocket, but a delayed or lost broadcast used to strand students in the
// lobby indefinitely. While the status modal is open we poll the attempt's
// current status and feed any change through the same handler the broadcast
// would have used.
const LOBBY_POLL_MS = 15000;
let lobbyPollTimer = null;
let lobbyPollInFlight = false;

// What the page is currently showing. While the lobby modal is up that is the
// last status we were told about, and any difference is a change to apply.
// After a hard navigation there is no modal and no broadcast, and the pre-fix
// baseline of '' was wrong in both directions: it made the instructor's "end"
// (also '') look like the status quo, and made every other status look like a
// change — which would re-open the redirect prompt on every reconnect for an
// attempt the student is already sitting on. The attempt page renders from the
// QuizAttempt store module, so that is the honest baseline; when there is none
// (a student on an ordinary page) there is nothing to compare against and the
// re-sync stays silent.
const renderedAttemptStatus = () => {
    const quizId = $store.getters['User/get']('quiz_id');

    return $store.state.QuizAttempt?.[quizId]?.quiz_attempt?.status ?? null;
};

const shouldApplyPolledStatus = polledStatus => {
    const displayedStatus = quizAttempt.value?.status ?? '';

    if (quizAttempt.value) {
        return polledStatus !== displayedStatus;
    }

    const renderedStatus = renderedAttemptStatus();

    return renderedStatus !== null && polledStatus !== renderedStatus;
};

const pollLobbyStatus = async () => {
    if (lobbyPollInFlight || !lobbyAttemptId.value) return;

    lobbyPollInFlight = true;
    const attemptId = lobbyAttemptId.value;
    const statusRevision = quizStatusRevision();

    try {
        const attempt = await quizWaitingRoomService.getStatus(attemptId);
        if (disposed || lobbyAttemptId.value !== attemptId || quizStatusRevision() !== statusRevision) return;
        const polledStatus = attempt?.status ?? '';

        if (shouldApplyPolledStatus(polledStatus)) {
            await applyQuizStatus({
                user: { ...user, quiz_active_id: lobbyAttemptId.value },
                status: polledStatus,
                quiz_attempt_id: lobbyAttemptId.value,
            });
        }
    } catch (_e) {
        // Fallback path only — a failed poll must never disturb the lobby UI.
    } finally {
        lobbyPollInFlight = false;
    }
};

// The first broadcast may precede subscription. The existing online heartbeat
// supplies the new attempt ID even when this page has never opened a lobby.
useQuizAttemptDiscovery({
    onlineResponse: () => props.onlineResponse,
    routeName: () => $route.name,
    shouldDiscover: attemptId =>
        typeof $route.name === 'string' &&
        !$route.name.includes('attempt') &&
        renderedAttemptStatus() === null &&
        !isStatusModalOpen.value &&
        !isRedirectPromptOpen.value &&
        !isClosingModalOpen.value &&
        !hasTransitionError.value &&
        (!activeAttemptId() || activeAttemptId() === attemptId),
    onDiscovered: attempt =>
        applyQuizStatus({
            user: { ...user, quiz_active_id: attempt.id },
            status: attempt.status,
            quiz_attempt_id: attempt.id,
        }),
});

const stopLobbyPoll = () => {
    if (lobbyPollTimer) {
        clearInterval(lobbyPollTimer);
        lobbyPollTimer = null;
    }
};

watch(isStatusModalOpen, open => {
    stopLobbyPoll();

    if (open && lobbyAttemptId.value) {
        lobbyPollTimer = setInterval(pollLobbyStatus, LOBBY_POLL_MS);
    }
});

onUnmounted(() => {
    disposed = true;
    stopLobbyPoll();
    unregisterStatusController();
});

if (user) {
    useSocketConnection({
        channel,
        event: 'QuizAttemptForceStatus',
        callback: async event => {
            const accepted = await applyQuizStatus(event);
            if (accepted && event.quiz_attempt_id) {
                lobbyAttemptId.value = event.quiz_attempt_id;
                if (isStatusModalOpen.value && !lobbyPollTimer)
                    lobbyPollTimer = setInterval(pollLobbyStatus, LOBBY_POLL_MS);
            }
        },
        // A status broadcast may have fired while the socket was down; poll the
        // attempt once on reconnect so the student is released immediately
        // instead of waiting for the next lobby-poll tick.
        //
        // Seed the attempt id first. Only a received broadcast ever sets it, and
        // a student who dropped mid-quiz has received none in this page session,
        // so the poll was a guaranteed no-op on the one page AC-5 is about.
        onReconnect: () => {
            lobbyAttemptId.value = lobbyAttemptId.value ?? activeAttemptId();

            return pollLobbyStatus();
        },
    });
}
</script>
